/**
 * Gjendja e identifikimit: përdoruesi, roli (custom claim) dhe profili te perdorues/{uid}.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onIdTokenChanged, signOut, type User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import type { Perdorues, Roli } from '@albmedtech/shared';
import { auth, db } from '@/firebase';

interface GjendjaAuth {
  ngarkohet: boolean;
  perdoruesi: User | null;
  roli: Roli | null;
  institucioniId: string | null;
  profili: (Perdorues & { id: string }) | null;
  dil: () => Promise<void>;
  rifreskoToken: () => Promise<void>;
}

const Konteksti = createContext<GjendjaAuth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ngarkohet, setNgarkohet] = useState(true);
  const [perdoruesi, setPerdoruesi] = useState<User | null>(null);
  const [roli, setRoli] = useState<Roli | null>(null);
  const [institucioniId, setInstitucioniId] = useState<string | null>(null);
  const [profili, setProfili] = useState<GjendjaAuth['profili']>(null);

  useEffect(
    () =>
      onIdTokenChanged(auth, async (u) => {
        setPerdoruesi(u);
        if (u) {
          const t = await u.getIdTokenResult();
          setRoli((t.claims.roli as Roli | undefined) ?? null);
          setInstitucioniId((t.claims.institucioniId as string | undefined) ?? null);
        } else {
          setRoli(null);
          setInstitucioniId(null);
          setProfili(null);
        }
        setNgarkohet(false);
      }),
    [],
  );

  useEffect(() => {
    if (!perdoruesi) return;
    return onSnapshot(
      doc(db, 'perdorues', perdoruesi.uid),
      (s) => setProfili(s.exists() ? ({ id: s.id, ...(s.data() as Perdorues) }) : null),
      () => setProfili(null),
    );
  }, [perdoruesi]);

  const vlera = useMemo<GjendjaAuth>(
    () => ({
      ngarkohet,
      perdoruesi,
      roli,
      institucioniId,
      profili,
      dil: () => signOut(auth),
      rifreskoToken: async () => {
        await auth.currentUser?.getIdToken(true);
      },
    }),
    [ngarkohet, perdoruesi, roli, institucioniId, profili],
  );
  return <Konteksti.Provider value={vlera}>{children}</Konteksti.Provider>;
}

export function useAuth(): GjendjaAuth {
  const k = useContext(Konteksti);
  if (!k) throw new Error('useAuth duhet brenda <AuthProvider>');
  return k;
}

export const ESHTE_BRENDSHEM: Roli[] = ['superadmin', 'staf', 'financa'];
